<template>
  <ValidationObserver ref="customerForm" v-slot="{ invalid, passes }">
    <v-skeleton-loader :loading="loading" :types="skeleton" type="article, actions" class="mb-6">
      <v-form rel="customerForm" @submit.prevent="passes(save)">
        <v-card class="elevation-1 mb-6 rounded-lg">
          <v-card-title class="pa-7">
            {{ title }}
          </v-card-title>
          <v-card-text class="pl-7 pr-7 pt-7">
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VTextFieldWithValidation
                  v-model="value.name"
                  rules="required"
                  outlined
                  dense
                  label="Název"
                  data-cy="name"
                  maxlength="70"
                  counter
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VTextAreaWithValidation
                  v-model="value.description"
                  rules="required"
                  outlined
                  dense
                  label="Popis"
                  data-cy="name"
                  auto-grow
                  counter
                  maxlength="150"
                  rows="3"
                />
              </v-col>
            </v-row>
            <v-row v-if="logoUrlBase64" class="mb-6">
              <v-col cols="12" sm="2" class="py-0 px-5">
                <v-img :src="logoUrlBase64" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VFileInputWithValidation
                  v-model="files"
                  :rules="{
                    required: uploadLogoRequired,
                    ext: ['png'],
                  }"
                  prepend-icon=""
                  append-icon="$file"
                  :clearable="false"
                  outlined
                  counter
                  :show-size="1000"
                  :label="uploadLogoLabel"
                  @change="setFile($event)"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VTextFieldWithValidation
                  v-model="value.btnActionName"
                  rules="required"
                  outlined
                  dense
                  label="Název akčního tlačítka"
                  data-cy="name"
                  maxlength="50"
                  counter
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <GiftAutocomplete v-model="value.giftId" :items="gifts" :rules="''" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VAutocompleteWithValidation
                  v-model="value.formType"
                  label="Typ formuláře"
                  rules="required"
                  outlined
                  dense
                  background-color="white"
                  :items="promotionFormTypes"
                  item-text="text"
                  item-value="name"
                  :loading="promotionFormTypesLoading"
                />
              </v-col>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VAutocompleteWithValidation
                  v-model.trim="value.sources"
                  label="Zdroje"
                  rules="required"
                  outlined
                  dense
                  multiple
                  :custom-slot="true"
                  background-color="white"
                  :items="promotionSources"
                  item-text="text"
                  item-value="name"
                  :loading="promotionSourcesLoading"
                >
                  <template slot="item" slot-scope="{ item }">
                    {{ item.text }}
                  </template>
                  <template slot="selection" slot-scope="{ index, item }">
                    <span v-if="index === 0">
                      <template v-if="value.sources.length === 1">
                        {{ item.text }}
                      </template>
                      <template v-else-if="value.sources.length > 1 && value.sources.length < 5">
                        Vybrány {{ value.sources.length }} položky
                      </template>
                      <template v-else-if="value.sources.length > 4">
                        Vybráno {{ value.sources.length }} položek
                      </template>
                    </span>
                  </template>
                </VAutocompleteWithValidation>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VDatePickerWithValidation
                  v-model="value.startAt"
                  rules="required"
                  date-format="dd.MM.yyyy"
                  outlined
                  dense
                  label="Začátek"
                />
              </v-col>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VDatePickerWithValidation
                  v-model="value.endAt"
                  date-format="dd.MM.yyyy"
                  rules="required"
                  outlined
                  dense
                  label="Ukončení"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <VTextFieldWithValidation
                  v-model="value.showIn"
                  type="number"
                  min="1"
                  outlined
                  dense
                  label="Zobrazit po"
                  suffix="s"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4" class="py-0 px-5">
                <div class="request-label">Stav</div>
                <div class="request-value mt-0">
                  <v-checkbox
                    v-model="value.enabled"
                    :label="`${value.enabled ? 'Zapnuto' : 'Vypnuto'}`"
                  ></v-checkbox>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="pa-7">
            <v-spacer></v-spacer>
            <v-btn
              type="submit"
              height="40px"
              min-width="130px"
              color="primary"
              depressed
              :loading="saving"
            >
              Uložit
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-skeleton-loader>
  </ValidationObserver>
</template>

<script>
import GiftAutocomplete from './inputs/GiftAutocomplete'
import { mapActions } from 'vuex'
import VAutocompleteWithValidation from '../validators/VAutocompleteWithValidation'
import VDatePickerWithValidation from '../validators/VDatePickerWithValidation'
import VTextAreaWithValidation from '../validators/VTextAreaWithValidation'

export default {
  name: 'PromotionForm',
  components: {
    VTextAreaWithValidation,
    VDatePickerWithValidation,
    VAutocompleteWithValidation,
    GiftAutocomplete,
  },
  props: {
    value: {
      type: Object,
    },
    logoUrlBase64: {
      type: String,
      default: '',
      required: false,
    },
    title: {
      type: String,
      default: '',
    },
    edit: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },
    saving: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      skeleton: {
        article: 'heading, paragraph@2',
      },
      gifts: [],
      promotionSources: [],
      promotionSourcesLoading: false,
      promotionFormTypes: [],
      promotionFormTypesLoading: false,
      files: null,
    }
  },
  computed: {
    uploadLogoLabel() {
      return this.edit ? 'Nahrajte nové logo (.png)' : 'Nahrajte logo (.png)'
    },
    uploadLogoRequired() {
      return !this.edit
    },
  },
  created() {
    this.loadGiftPromotionSource()
    this.loadGiftPromotionFormTypes()
  },
  methods: {
    ...mapActions({
      getGiftPromotionSource: 'enum/listGiftPromotionSource',
      getGiftPromotionFormType: 'enum/listGiftPromotionFormTypes',
    }),
    loadGiftPromotionSource() {
      this.promotionSourcesLoading = true
      this.getGiftPromotionSource()
        .then((response) => {
          this.promotionSources = response.data
        })
        .finally(() => {
          this.promotionSourcesLoading = false
        })
    },
    loadGiftPromotionFormTypes() {
      this.promotionFormTypesLoading = true
      this.getGiftPromotionFormType()
        .then((response) => {
          this.promotionFormTypes = response.data
        })
        .finally(() => {
          this.promotionFormTypesLoading = false
        })
    },
    setFile(event) {
      this.files = event
    },
    save() {
      this.$emit('save', { value: this.value, files: this.files })
    },
  },
}
</script>
